import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CloudRain,
  Flame,
  Maximize,
  Minimize,
  Radio,
  Waves,
  Wind,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { tickerStyle } from "@/lib/ticker";
import MapPanel from "@/components/MapPanel";
import HotspotTrend from "@/components/HotspotTrend";
import IspuTrend from "@/components/IspuTrend";
import IspuDetail from "@/components/IspuDetail";
import AlertList from "@/components/AlertList";

const SLIDE_MS = 15000;
const KLHK_SLIDE_MS = 30000; // slide KLHK lebih lama: 6 kecamatan bergiliran @5 dtk
const SLIDES = ["Metrik Utama", "Tren Hotspot 7 Hari", "Tren ISPU 7 Hari", "Grafik ISPU (KLHK)", "Peringatan & Wilayah"];
const SLIDE_DUR = SLIDES.map((s) => (s === "Grafik ISPU (KLHK)" ? KLHK_SLIDE_MS : SLIDE_MS));

export default function TvMode() {
  const [data, setData] = useState({ districts: [], alerts: [], summary: {}, source: "-" });
  const [hotspots, setHotspots] = useState([]);
  const [kalbarHotspots, setKalbarHotspots] = useState([]);
  const [trend, setTrend] = useState([]);
  const [ispuTrend, setIspuTrend] = useState({ items: [], districts: [] });
  const [slide, setSlide] = useState(0);
  const [clock, setClock] = useState(new Date());
  const [isFull, setIsFull] = useState(false);
  const [ticker, setTicker] = useState({ text: "", enabled: false, speed: "sedang", size: "sedang" });

  useEffect(() => {
    const load = () =>
      api
        .get("/settings")
        .then((r) =>
          setTicker({
            text: r.data.ticker_text || "",
            enabled: Boolean(r.data.ticker_enabled),
            speed: r.data.ticker_speed || "sedang",
            size: r.data.ticker_size || "sedang",
          }),
        )
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [m, h, k, t, it] = await Promise.all([
          api.get("/monitoring"),
          api.get("/hotspots", { params: { date_filter: "today_wib" } }),
          api.get("/hotspots/kalbar", { params: { date_filter: "today_wib" } }),
          api.get("/hotspots/trend"),
          api.get("/ispu/trend"),
        ]);
        setData(m.data);
        setHotspots(h.data.items || []);
        setKalbarHotspots(k.data.items || []);
        setTrend(t.data.items || []);
        setIspuTrend(it.data || { items: [], districts: [] });
      } catch (e) {
        console.error(e);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSlide((s) => (s + 1) % SLIDES.length), SLIDE_DUR[slide]);
    return () => clearTimeout(t);
  }, [slide]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onFs = () => setIsFull(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => {});
  };

  const districtsWithCounts = useMemo(() => {
    const byD = {};
    hotspots.forEach((h) => {
      byD[h.district] = (byD[h.district] || 0) + 1;
    });
    return data.districts
      .map((d) => ({ ...d, hotspot_count: byD[d.name] || 0 }))
      .sort((a, b) => b.hotspot_count - a.hotspot_count);
  }, [data.districts, hotspots]);

  const summary = data.summary || {};
  const highRiskCount = districtsWithCounts.filter((d) => d.overall_risk === "TINGGI").length;

  return (
    <div className="tv-frame" data-testid="tv-mode">
      {ticker.enabled && ticker.text && (
        <div className="ticker-bar tv-ticker" data-testid="tv-ticker-bar">
          <span className="ticker-badge">INFO</span>
          <div className="ticker-scroll">
            <p style={tickerStyle(ticker.speed, ticker.size, 4)}>
              {ticker.text} &nbsp;&nbsp;•&nbsp;&nbsp; {ticker.text}
            </p>
          </div>
        </div>
      )}
      <header className="tv-topbar">
        <div className="brand">
          <div className="brand-mark">
            <Radio size={20} />
          </div>
          <div>
            <b>SITAKO · MODE TV</b>
            <span>PUSDALOPS BPBD · KAYONG UTARA</span>
          </div>
        </div>
        <div className="tv-clock" data-testid="tv-clock">
          {clock.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          <small>
            {clock.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}{" "}
            WIB
          </small>
        </div>
        <div className="live-state">
          <i /> LIVE · {data.source}
        </div>
        <button
          className="admin-button"
          onClick={toggleFullscreen}
          data-testid="tv-fullscreen-btn"
        >
          {isFull ? <Minimize size={15} /> : <Maximize size={15} />}
          {isFull ? "Keluar Layar Penuh" : "Layar Penuh"}
        </button>
        <Link to="/" className="admin-button" data-testid="tv-exit-btn">
          <X size={15} /> Tutup
        </Link>
      </header>

      <div className="tv-body">
        <div className="tv-map" data-testid="tv-map">
          <MapPanel
            hotspots={hotspots}
            kalbarHotspots={kalbarHotspots}
            districts={districtsWithCounts}
            reports={[]}
            selectedDistrict="Semua wilayah"
          />
        </div>

        <aside className="tv-side" data-testid="tv-side-panel">
          <div className="tv-slide-head">
            <span className="section-kicker">{SLIDES[slide]}</span>
            <div className="tv-dots">
              {SLIDES.map((s, i) => (
                <button
                  key={s}
                  className={i === slide ? "on" : ""}
                  onClick={() => setSlide(i)}
                  aria-label={s}
                  data-testid={`tv-dot-${i}`}
                />
              ))}
            </div>
          </div>
          <div className="tv-progress" key={slide}>
            <i style={{ animationDuration: `${SLIDE_DUR[slide]}ms` }} />
          </div>

          <div className="tv-slide" key={`slide-${slide}`} data-testid={`tv-slide-${slide}`}>
            {slide === 0 && (
              <div className="tv-metrics">
                <TvTile icon={<Flame />} tone="fire" label="Hotspot aktif hari ini" value={hotspots.length} unit="titik" />
                <TvTile
                  icon={<CloudRain />}
                  tone="rain"
                  label="Rata-rata hujan"
                  value={summary.is_raining ? summary.rain_now : "—"}
                  unit={summary.is_raining ? "mm" : ""}
                  note={
                    summary.is_raining
                      ? `${summary.raining_district_count} kecamatan hujan`
                      : "Tidak ada hujan"
                  }
                />
                <TvTile
                  icon={<Waves />}
                  tone="wave"
                  label="Gelombang pasang"
                  value={summary.wave_now ?? "—"}
                  unit={summary.wave_now != null ? "m" : ""}
                  note={summary.wave_cat || ""}
                />
                <TvTile
                  icon={<Wind />}
                  tone={summary.ispu > 100 ? "alert" : "wave"}
                  label="ISPU udara"
                  value={summary.ispu ?? "—"}
                  note={summary.ispu_category || ""}
                />
                <TvTile
                  icon={<AlertTriangle />}
                  tone="alert"
                  label="Wilayah risiko tinggi"
                  value={highRiskCount}
                  unit="wilayah"
                  wide
                />
              </div>
            )}
            {slide === 1 && <HotspotTrend data={trend} />}
            {slide === 2 && (
              <IspuTrend data={ispuTrend.items} districts={ispuTrend.districts} compact />
            )}
            {slide === 3 && <IspuDetail compact autoRotate />}
            {slide === 4 && (
              <div className="tv-alerts">
                <AlertList alerts={data.alerts} />
                <div className="tv-district-chips">
                  {districtsWithCounts.map((d) => (
                    <div
                      key={d.name}
                      className={`tv-chip risk-${(d.overall_risk || "RENDAH").toLowerCase()}`}
                    >
                      <b>{d.name}</b>
                      <span>
                        {d.hotspot_count} titik · {d.overall_risk || "RENDAH"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function TvTile({ icon, tone, label, value, unit, note, wide }) {
  return (
    <div className={`tv-tile ${wide ? "tv-tile-wide" : ""}`}>
      <div className={`metric-icon ${tone}`}>{icon}</div>
      <span>{label}</span>
      <strong>
        {value}
        {unit ? <em> {unit}</em> : null}
      </strong>
      {note && <small>{note}</small>}
    </div>
  );
}
